import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue';
import type { Car } from '@/entities/cars/car.entity';
import type { BookingLocation } from '@/entities/bookings/booking.entity';
import { getOperationalStationOfCar } from '@/hooks/use-current-station-of-car';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useLegalEntities } from '@/queries/use-legal-entities';
import { useStations } from '@/queries/use-stations';
import { DEFAULT_TAX_LEGAL_ENTITY, type TaxLegalEntity } from '@/utils/resolve-tax-country';

/** Country and OSS registration of a legal entity; the default until it is known and loaded. */
export const useTaxLegalEntity = (
  legalEntityId: MaybeRefOrGetter<string | null | undefined>,
): ComputedRef<TaxLegalEntity> => {
  const { data: legalEntities } = useLegalEntities();
  return computed(
    () =>
      legalEntities.value?.find((legalEntity) => legalEntity.id === toValue(legalEntityId)) ??
      DEFAULT_TAX_LEGAL_ENTITY,
  );
};

/**
 * Legal entity a new booking gets: the one of the car's operational station at pickup, as the
 * backend picks it, else the one of the pickup station.
 */
export const usePickupTaxLegalEntity = (params: {
  car: MaybeRefOrGetter<Car | null | undefined>;
  startDate: MaybeRefOrGetter<Date | string>;
  pickupLocation: MaybeRefOrGetter<BookingLocation | null | undefined>;
}): ComputedRef<TaxLegalEntity> => {
  const { data: stationsData } = useStations(500);
  const stations = useFlattenPaginatedData(stationsData);

  const legalEntityId = computed(() => {
    const car = toValue(params.car);
    const pickupStation = toValue(params.pickupLocation)?.station;
    // Without a primary station the operational station cannot be resolved.
    if (!car?.stations?.some((carStation) => carStation.primary !== false)) {
      return pickupStation?.legalEntity?.id;
    }
    const station = getOperationalStationOfCar(
      car,
      toValue(params.startDate),
      pickupStation ? [pickupStation.id] : [],
    );
    // Car stations come without their legal entity; the station list has it.
    return (
      stations.value.find(({ id }) => id === station.id)?.legalEntity?.id ??
      pickupStation?.legalEntity?.id
    );
  });

  return useTaxLegalEntity(legalEntityId);
};
